import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, lastValueFrom } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public showLoader: boolean = false;
  private _hasReserve = false;
  constructor(private http: HttpClient, private router: Router) {
    const saved = sessionStorage.getItem('HAS_RESERVE');
    this._hasReserve = saved === 'true';
  }

  getUser(): string {
    return sessionStorage.getItem('SESSION-USER') ?? '';
  }

  userIsAuth(): boolean {
    return (
      sessionStorage.getItem('SESSION-USER') !== null &&
      this.getAcessToken() !== null &&
      this.getRefreshToken() !== null
    );
  }

  getAcessToken(): string {
    return JSON.parse(this.getUser()).accessToken;
  }

  getRefreshToken(): string {
    return JSON.parse(this.getUser()).refreshToken;
  }

  getName(): string {
    return sessionStorage.getItem('name') ?? '';
  }

  getCompany(): string {
    return sessionStorage.getItem('company') ?? '';
  }

  getOriginCurrency(): string {
    return this.getAcessTokenInformation()?.originCurrency ?? 'BRL';
  }

  loginWithEmail(credentials: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/login`, credentials);
  }

  needChangePassword(): boolean {
    return (
      this.getAcessTokenInformation()!.securityStep == undefined ||
      this.getAcessTokenInformation()!.securityStep.indexOf('SET_PASSWORD') ==
        -1
    );
  }

  needChange2FA(): boolean {
    return (
      this.getAcessTokenInformation()!.securityStep == undefined ||
      this.getAcessTokenInformation()!.securityStep.indexOf('SET_2FA') == -1
    );
  }

  changeSecurityStep(step: string, isAdd: boolean) {
    this.http
      .post<any>(`${environment.apiUrl}/changeStepSecurity`, {
        step: step,
        isAdd: isAdd,
      })
      .subscribe();
  }

  isAdmin(): boolean {
    if (!this.userIsAuth()) {
      return false;
    }
    return this.getAcessTokenInformation()?.role.indexOf('Administrator')! > -1;
  }

  isReadOnly(): boolean {
    if (!this.userIsAuth()) {
      return false;
    }
    return this.getAcessTokenInformation()?.role.indexOf('ReadOnly')! > -1;
  }

  isNoCrypto(): boolean {
    if (!this.userIsAuth()) {
      return false;
    }
    return this.getAcessTokenInformation()?.role.indexOf('NoCrypto')! > -1;
  }

  public isWeb(): boolean {
    return this.getAcessTokenInformation()!.scope === 'WEB';
  }

  callRefreshToken() {
    if (!this.userIsAuth()) {
      return;
    }

    this.http
      .post<any>(`${environment.apiUrl}/token/refresh`, {
        token: this.getRefreshToken(),
      })
      .subscribe({
        next: (user) => {
          this.setUserLoggedIn(user);
        },
        error: async (err) => {
          if (err.status === 401) {
            sessionStorage.removeItem('SESSION-USER');
            await this.router.navigate(['/']);
          }
        },
      });
  }

  revokeToken() {
    if (!this.userIsAuth()) {
      return;
    }
    let email = this.getAcessTokenInformation()!.email;
    lastValueFrom(
      this.http.post<any>(`${environment.apiUrl}/token/revoke`, {
        token: this.getRefreshToken(),
        email: email,
      })
    ).then();
  }

  clearStorage(): void {
    sessionStorage.clear();
  }

  updatePassword(password: string): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/users`, {
      password: password,
    });
  }

  preloadAccountInfo(): void {
    this.http.get<any>(`${environment.apiUrl}/account`).subscribe({
      next: acc => this.setReserveAccount(!!acc?.listsAccountTef?.length),
      error: () =>      this.setReserveAccount(false)
    });
  }

  // public getHasReserveAccount(): boolean {
  //   return this.hasReserveAccount;
  // }

  setReserveAccount(value: boolean): void {
    this._hasReserve = value;
  }

 hasReserveAccount(): boolean {
    return this._hasReserve;
  }

  public validate(email: string, senha: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Basic ' + btoa(`${email}:${senha}`),
    });

    return this.http.head<any>(`${environment.apiUrl}/validate/${email}`, {
      headers,
      observe: 'response',
    });
  }

  needTotp() {
    return sessionStorage.getItem('need2FA') !== null;
  }

  totpValidated() {
    sessionStorage.removeItem('need2FA');
  }

  public setUserLoggedIn(response: any) {
    let token = {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    };
    sessionStorage.setItem('SESSION-USER', JSON.stringify(token));
    sessionStorage.setItem('name', this.getAcessTokenInformation()!.name);
    sessionStorage.setItem('company', this.getAcessTokenInformation()!.company);
    sessionStorage.setItem('need2FA', 'x');
  }

  resetPassword(email: string): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/users/reset`, {
      email: email,
    });
  }
  private getAcessTokenInformation(): JwtData | undefined {
    if (this.userIsAuth()) {
      return this.decodeToken(this.getAcessToken());
    }

    return undefined;
  }

  public decodeToken(token: string): JwtData {
    return jwtDecode<JwtData>(token);
  }

  getTotpManualCode(email: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${environment.apiUrl}/totp/code/${email}`)
    );
  }
  getTotpQrCode(email: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${environment.apiUrl}/totp/qrCode/${email}`)
    );
  }

  validateTotpCode(code: string, email?: string): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${environment.apiUrl}/totp/validate-code`, {
        email: email,
        code: code,
      })
    );
  }

  public isCallRefreshToken(): boolean {
    if (this.userIsAuth() && this.getAcessTokenInformation() !== null) {
      const currentTime = Math.floor(Date.now() / 1000);
      const expirationTime = this.getAcessTokenInformation()!.exp;
      const remaingTimeToExpiration = expirationTime - currentTime;
      return remaingTimeToExpiration <= 180;
    }

    return false;
  }

  public getAccountId(): string {
    return this.getAcessTokenInformation()!.accountId;
  }
}

export interface JwtData {
  email: string;
  accountId: string;
  name: string;
  scope: string;
  company: string;
  role: string[];
  securityStep: string[];
  exp: number;
  iss: string;
  aud: string;
  originCurrency: string;
}
